import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { getCart, Cart } from '../utils/cart';
import ShoppingCart from './ShoppingCart';
import { useCart } from '../contexts/CartContext';

const CartButton: React.FC = () => {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cart, setCart] = useState<Cart | null>(null);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const { error, clearError } = useCart();
  
  // Load cart on mount and update when cart is closed
  useEffect(() => {
    const loadCart = () => {
      const currentCart = getCart();
      setCart(currentCart);
    };
    
    loadCart();
    
    // Set up a timer to periodically check for cart updates
    const intervalId = setInterval(loadCart, 2000);
    
    return () => clearInterval(intervalId);
  }, []);
  
  // Handle error display
  useEffect(() => {
    if (error) {
      setShowErrorToast(true);
      // Auto-dismiss after 3 seconds
      const timer = setTimeout(() => {
        setShowErrorToast(false);
        clearError();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);
  
  // Update cart when cart is closed
  const handleCloseCart = () => {
    setIsCartOpen(false);
    const currentCart = getCart();
    setCart(currentCart);
  };
  
  return (
    <>
      {showErrorToast && (
        <div className="fixed top-5 right-5 z-[9999] bg-red-900/90 border border-red-500 p-3 rounded-lg shadow-lg max-w-xs transition-opacity duration-300">
          <div className="flex items-start">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-300 mr-3 mt-1 flex-shrink-0" />
            <p className="text-red-300 text-sm">{error}</p>
            <button 
              className="ml-2 text-red-300 hover:text-white" 
              onClick={() => {
                setShowErrorToast(false);
                clearError();
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}
      
      <button
        className="relative p-2 text-accent hover:text-accent/80 transition-colors"
        onClick={() => setIsCartOpen(true)}
        aria-label="Open cart"
      >
        <FontAwesomeIcon icon={faShoppingCart} className="w-5 h-5" />
        
        {cart && cart.totalItems > 0 && (
          <span className="absolute -top-1 -right-1 bg-accent text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {cart.totalItems}
          </span>
        )}
      </button>
      
      <ShoppingCart isOpen={isCartOpen} onClose={handleCloseCart} />
    </>
  );
};

export default CartButton; 